import {
  FixedFeeConfig,
  Adjustment,
  ReclassBalance,
} from "@amzn/fuse-accounting-central-datastore-client";
import { ReportRow } from "./types";
import { toNum, getFirstOfMonth } from "./utils";

export function computeReportRow(
  config: FixedFeeConfig,
  reportDate: string,
  reclassCutoff: string,
): ReportRow | null {
  const env = config.environment;
  const status = config.dealStatus;
  if (env !== "PRODUCTION" || status !== "ENABLED") return null;

  const dealStart = config.dealStartDate ?? "";
  const dealEnd = config.dealEndDate ?? "";
  const reportMonthStart = getFirstOfMonth(reportDate);

  // Deal must be active during report month
  if (dealEnd && dealEnd < reportMonthStart) return null;
  if (dealStart && dealStart > reportDate) return null;

  const contractPrice = toNum(config.contractPrice);
  const rb: ReclassBalance | undefined = config.reclassBalance;
  const hasReclass =
    rb != null &&
    (rb.unbilledAccountsReceivables != null || rb.longTermReceivables != null);

  const ltIssuance = hasReclass
    ? rb!.longTermReceivables != null
      ? toNum(rb!.longTermReceivables)
      : 0
    : 0;
  const stIssuance = hasReclass
    ? rb!.unbilledAccountsReceivables != null
      ? toNum(rb!.unbilledAccountsReceivables)
      : contractPrice - ltIssuance
    : contractPrice;

  // Invoices issued: invoice date (map key) <= report date
  const invoiceBreakdown = config.invoiceBreakdown ?? {};
  let invoicesIssued = 0;
  for (const [invoiceDate, detail] of Object.entries(invoiceBreakdown)) {
    if (invoiceDate <= reportDate) {
      invoicesIssued += toNum(detail.invoiceAmount);
    }
  }

  // Cumulative reclassification: sum receivableShiftAmount where adj date <= reclassCutoff
  const adjustments: Record<string, Adjustment> = config.adjustments ?? {};
  let cumulativeReclass = 0;
  for (const [adjDate, adj] of Object.entries(adjustments)) {
    if (adjDate <= reclassCutoff && adj.receivableShiftAmount != null) {
      cumulativeReclass += toNum(adj.receivableShiftAmount);
    }
  }

  const resellerId = config.dealConfig?.resellerIds?.[0] ?? "";

  // Automation Status: "Automatic" only if reclassification is automated (receivableShiftAmount present)
  // AND invoices are being issued automatically (invoiceBreakdown non-empty)
  const hasAutoReclass = Object.values(adjustments).some(
    (adj) => adj.receivableShiftAmount != null,
  );
  const hasAutoInvoice = Object.keys(invoiceBreakdown).length > 0;
  const effortType = hasAutoReclass && hasAutoInvoice ? "Automatic" : "Manual";

  // First IB key month = boundary for tracker records belonging to this config
  const ibKeys = Object.keys(invoiceBreakdown).sort();
  const firstInvoiceBreakdownPeriod =
    ibKeys.length > 0 ? ibKeys[0].substring(0, 7) : "";

  return {
    fixedFeeId: config.fixedFeeId ?? "",
    companyCode: "",
    resellerId,
    region: "",
    dealStartDate: dealStart,
    dealEndDate: dealEnd,
    effortType,
    contractPrice,
    currencyCode: config.contractCurrencyCode ?? "",
    // FACD FixedFeeConfig will add exchangeRate field for locked-rate deals; 0 = not yet available
    fxRate: toNum((config as any).exchangeRate) || 0,
    funcCurrency: config.contractCurrencyCode ?? "",
    stIssuance,
    ltIssuance,
    cumulativeReclass,
    invoicesIssued,
    functionalInvoicesIssued: 0,
    stEnding: stIssuance + cumulativeReclass - invoicesIssued,
    ltEnding: ltIssuance - cumulativeReclass,
    funcStIssuance: 0,
    funcStReclass: 0,
    funcStEnding: 0,
    stFxReval: 0,
    funcLtIssuance: 0,
    funcLtReclass: 0,
    funcLtEnding: 0,
    ltFxReval: 0,
    firstInvoiceBreakdownPeriod,
    invoiceStatus: "unreconciled" as const,
    trackerTotal: 0,
  };
}
